import styled from "styled-components";
import { ReactComponent as Close } from "../../assets/svgIcon/listView/Close.svg";

/**
 * GeneralModal Component
 *
 * A highly flexible modal component that accepts custom children content and
 * supports multiple control options like Cancel, Reset, and Submit buttons.
 * Can be used across various modules (Milestones, Filters, Reports) to maintain consistent UI.
 *
 * @component
 *
 * @param {Function} closeModal - Function to close the modal when Cancel or close icon is clicked.
 * @param {string} btnName - Text to display on the primary action button (e.g., "Save", "Submit").
 * @param {React.ReactNode} children - The inner content of the modal body.
 * @param {string} titleOfModal - Title text shown at the top of the modal.
 * @param {Function} handleModalWork - Function called when the primary action button is clicked.
 * @param {string|number} widthOfGeneralModal - Sets the width of the modal container (e.g., "45vw").
 * @param {boolean} isChecked - Optional toggle to enable or disable the main action button.
 * @param {boolean} isCancelButttonShow - If true, shows a Cancel button.
 * @param {boolean} isResetButtonShow - If true, shows a Reset button.
 * @param {Function} resetModal - Function to execute when Reset is clicked.
 * @param {boolean} isDisabledResetBtn - If true, disables the Reset button.
 * @param {string} classNameOfModal - Additional CSS class names to apply on the modal container.
 * @param {string} titleForBtn - Optional tooltip text for the main action button.
 *
 */

export const GeneralModal = ({
  closeModal,
  btnName,
  children,
  titleOfModal,
  handleModalWork,
  widthOfGeneralModal,
  isChecked,
  isCancelButttonShow,
  isResetButtonShow,
  resetModal,
  isDisabledResetBtn,
  classNameOfModal,
  titleForBtn,
}) => {
  return (
    <Container width={widthOfGeneralModal} className={classNameOfModal}>
      <h1>
        {titleOfModal}
        <span onClick={closeModal}>
          {" "}
          <Close />
        </span>
      </h1>
      <div className="generalContent">{children}</div>
      <div className="createMilestone">
        {isCancelButttonShow && <button onClick={closeModal}>Cancel</button>}
        {isResetButtonShow && (
          <button className="reset" onClick={resetModal} disabled={isDisabledResetBtn}>
            Reset
          </button>
        )}
        {btnName && (
          <button
            onClick={handleModalWork}
            disabled={isChecked === undefined ? false : isChecked ? false : true}
            title={titleForBtn}
            className="create"
          >
            {btnName}
          </button>
        )}
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: ${props => props.width || "auto"};
  max-width: 85vw;
  left: 0;
  right: 0;
  margin: auto;
  background-color: white;
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 4px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05));

  h1 {
    height: 48px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    color: var(--DARK-GREY, #0e0e2c);
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 171.429% */
    border-radius: 4px 4px 0px 0px;
    border-bottom: 1px solid #e9edf5;
    background: #f9fafd;
    margin-bottom: 0;

    span {
      cursor: pointer;
    }
  }

  > .generalContent {
    flex: 1;
    padding: 8px 24px 16px 16px;
  }

  > .createMilestone {
    display: flex;
    justify-content: end;
    /* padding: 0 16px 8px; */
    padding: 0 24px 8px 16px;
    gap: 8px;

    button {
      border: 1px;
      padding: 4px 16px;
      border-radius: 4px;
    }
    .create {
      color: white;
      background: var(--BLUE, #2060ea);

      &:disabled {
        background-color: var(--MIST, #f7f8f9);
        color: var(--character-disabled-placeholder-25, rgba(0, 0, 0, 0.25));
      }
    }
    .reset {
      background: #cf324a;
      color: var(--Cloud, #fcfdfe);
      &:disabled {
        opacity: 0.25;
      }
    }
  }
  .buttonGroup {
    display: flex;
    justify-content: end;
    gap: 16px;
  }
`;
